import React, { useState, useEffect } from "react";
import { clearSession } from "../shared/_helpers/session";
import { Link, useNavigate } from "react-router-dom";
import { checkBlockedStatus, getProfile, updateProfile, removeProfileImage, getCountryCode } from "../shared/_services/common.services";
import { changePassword } from "../shared/_services/auth.services";
import { readCurrentUser } from "../shared/_helpers/currentUser";
import { hasUnlimitedMeetings, meetingsAvailableCount } from "../shared/_helpers/meetingEntitlement";
import { useSnackbar } from "notistack";
import { Formik, Form, Field as FormikField, ErrorMessage } from "formik";
import * as Yup from "yup";
import Subscription from "./Subscription";
import AdvisorReviewsCard from "./AdvisorReviewsCard";
import Loader from "./Loader";
import ConsentsCard from "./onboarding/ConsentsCard";
import LinkedAccountsCard from "./plaid/LinkedAccountsCard";
import { CHOOSE_PLAN_PATH, hasChatAccess } from "../shared/_helpers/chatGate";
import StarsIcon from "../styles/StarsIcon";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Field,
  Input,
  Page,
  PageHead,
  Panel,
  Select,
} from "./ui";

//updated code

function getProfileInitials(userData) {
  const firstInitial = userData?.firstName ? userData.firstName.trim().charAt(0) : "";
  const lastInitial = userData?.lastName ? userData.lastName.trim().charAt(0) : "";
  return `${firstInitial}${lastInitial}`;
}

function shouldShowProfileImage(previewImage, userData) {
  return Boolean(previewImage || (userData?.profile_image && userData.profile_image.trim() !== ""));
}

function getProfileImageSrc(previewImage, userData) {
  return previewImage || userData?.profile_image;
}

function redirectToLogin(navigate) {
  clearSession();
  navigate("/login");
}

async function runBlockedCheck(navigate) {
  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  try {
    const response = await checkBlockedStatus(userId);
    const isUnauthorized = response.data ? response.data.status : response.response.status == 401;

    if (isUnauthorized) {
      redirectToLogin(navigate);
      return;
    }

    const { isBlocked, isDeleted } = response.data;
    if (isBlocked || isDeleted) {
      redirectToLogin(navigate);
    }
  } catch (error) {
    console.error("Error checking blocked status:", error);
  }
}

async function fetchAndSetUserDetails(setShowProfileLoader, setUserData, navigate, enqueueSnackbar) {
  setShowProfileLoader(true);
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const userId = currentUser?.user?._id;
  const data = await getProfile(userId);
  setShowProfileLoader(false);

  if (data.status === 200) {
    setUserData(data?.data?.data?.user);
    return;
  }

  if (data?.response?.status === 401) {
    clearSession();
    enqueueSnackbar("Your session has been expired!", {
      variant: "error",
      anchorOrigin: { horizontal: "right", vertical: "top" },
    });
    navigate("/login");
  }
}

// D2: email and mobile are the login identifiers, so the server only lets a
// super-admin change them (403 CONTACT_CHANGE_NOT_ALLOWED otherwise).
export const CONTACT_CHANGE_BLOCKED_MESSAGE = "Contact support to change your email or phone.";

function isContactChangeRefused(result) {
  return (
    result?.response?.status === 403 &&
    result?.response?.data?.code === "CONTACT_CHANGE_NOT_ALLOWED"
  );
}

function buildProfileFormData(values) {
  const { name, email, mobile, profile_image, code } = values;
  const formData = new FormData();
  formData.append("firstName", name.split(" ")[0]);
  formData.append("lastName", name.split(" ").slice(1).join(" "));
  // D2: read-only fields, sent back exactly as stored (no lowercasing) and only
  // when present, together with the stored country code. The server refuses any
  // value that differs from what it holds, and an empty string would count as
  // "different" for a client who has no email or no phone.
  if (email) formData.append("email", email);
  if (mobile) formData.append("mobile", mobile);
  formData.append("code", code);

  if (profile_image instanceof File) {
    formData.append("profile_image", profile_image);
  }

  return formData;
}

function syncUpdatedUserToStorage(profileData) {
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const updatedLocalStorage = { ...currentUser };
  updatedLocalStorage.user = { ...currentUser.user, ...profileData };
  localStorage.setItem("currentUser", JSON.stringify(updatedLocalStorage));
  return updatedLocalStorage?.user?._id;
}

async function submitProfileUpdate(values, setSubmitting, deps) {
  const {
    setUserData,
    setPreviewImage,
    setShowEditProfile,
    fetchUserDetails,
    enqueueSnackbar,
  } = deps;

  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  const formData = buildProfileFormData(values);
  const data = await updateProfile(userId, formData);
  const responseUserData = data?.data?.data;

  syncUpdatedUserToStorage(responseUserData);
  setUserData((prevData) => ({
    ...prevData,
    data: { ...prevData.data, ...responseUserData },
  }));

  if (data.status == 200) {
    enqueueSnackbar("Profile updated successfully.", { variant: "success" });
  } else if (isContactChangeRefused(data)) {
    enqueueSnackbar(CONTACT_CHANGE_BLOCKED_MESSAGE, { variant: "error" });
  } else {
    enqueueSnackbar(data?.response?.data?.message || "Profile update failed", { variant: "error" });
  }

  setSubmitting(false);
  setPreviewImage(null);
  setShowEditProfile(true);
  fetchUserDetails();
}

async function removeProfileImageWithFeedback(setFieldValue, event, deps) {
  const { previewImage, setPreviewImage, enqueueSnackbar, setUserData } = deps;
  if (event) event.preventDefault();

  if (previewImage) {
    setPreviewImage(null);
    setFieldValue("profile_image", null);
    enqueueSnackbar("Preview Profile image removed successfully.", { variant: "success" });
    return;
  }

  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  const response = await removeProfileImage(userId);

  if (response.status === 200) {
    setPreviewImage(null);
    setFieldValue("profile_image", "");
    setUserData((prevData) => ({
      ...prevData,
      profile_image: "",
    }));
    enqueueSnackbar("Profile image removed successfully.", { variant: "success" });
    return;
  }

  enqueueSnackbar("Failed to remove profile image.", { variant: "error" });
}

// One password row: the label tied to the control, the show/hide toggle inside
// the field's right edge, Formik's error line below.
function PasswordField({ name, label, placeholder, shown, onToggle, error }) {
  return (
    <div className="tw-mb-4 tw-grid tw-gap-2">
      <label htmlFor={name} className="tw-text-sm tw-font-semibold tw-text-text">
        {label}
      </label>
      <div className="tw-relative">
        <FormikField
          as={Input}
          type={shown ? "text" : "password"}
          name={name}
          id={name}
          placeholder={placeholder}
          invalid={Boolean(error)}
          className="tw-pr-12"
        />
        <Button
          variant="icon"
          size="sm"
          iconOnly
          aria-label={shown ? "Hide password" : "Show password"}
          className="tw-absolute tw-inset-y-0 tw-right-2 tw-my-auto tw-border-transparent"
          onClick={onToggle}
        >
          <StarsIcon name={shown ? "eyeOff" : "eye"} className="tw-h-4 tw-w-4 tw-shrink-0" />
        </Button>
      </div>
      <ErrorMessage name={name} component="p" className="tw-m-0 tw-text-sm tw-text-danger" />
    </div>
  );
}

function ChangePasswordModal({
  show,
  onClose,
  enqueueSnackbar,
  showOldPassword,
  setShowOldPassword,
  showNewPassword,
  setShowNewPassword,
  showConfirmPassword,
  setShowConfirmPassword,
}) {
  return (
    <Dialog
      open={show}
      onOpenChange={(open) => { if (!open) onClose(); }}
    >
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
          <DialogTitle>Change Password</DialogTitle>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            aria-label="Close"
            className="tw-shrink-0"
            onClick={onClose}
          >
            <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
          </Button>
        </div>
        <DialogDescription className="tw-mb-6">
          Please provide your current password and set a
          new one to continue.
        </DialogDescription>
<Formik
  initialValues={{
    oldPassword: "",
    password: "",
    confirmPassword: "",
  }}
  validationSchema={Yup.object().shape({
    oldPassword: Yup.string().required("Current password is required"),
    password: Yup.string()
      .required("New password is required")
      .matches(
/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{8,}$/,
"Password should contain atleast 1 number, 1 lowercase, 1 uppercase, 1 special character and must of 8 digits."
      ),
    confirmPassword: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .matches(
/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{8,}$/,
"Password should contain atleast 1 number, 1 lowercase, 1 uppercase, 1 special character and must of 8 digits."
      ),
  })}
  onSubmit={async (values) => {
    const { password, oldPassword } = values;
    // Captured before the request, not after: localStorage is shared
    // across tabs, so a sign-out/sign-in to a DIFFERENT account in
    // another tab while this request is in flight must not let this
    // tab's response merge its fresh tokens into that other account's
    // stored identity — that would pair one account's token with a
    // different account's user object, a real identity/token
    // mismatch. Comparing the account id (not the full token) is
    // deliberate: this endpoint revokes every other session on THIS
    // account as part of a successful change, so the response's pair
    // is guaranteed valid for this account by construction. A
    // same-account re-sign-in from another tab racing this request is
    // a real but narrower case — comparing the token instead of the
    // id was tried and reverted (see PR #81 review) because it can't
    // tell "an intervening same-account session that is now stale"
    // from "one that's genuinely newer," and discarding the
    // guaranteed-valid response in favor of a possibly-already-revoked
    // one causes a worse failure: a false logout right after a
    // successful password change. No local signal can resolve that
    // ambiguity; it would need the server to hand back a session
    // identifier to order against.
    const accountIdBeforeChange = readCurrentUser()?.user?._id;
    const data = await changePassword(oldPassword, password);

    if (data.status == 200) {
      // SEC-084/SEC-115: a change revokes every session on the account,
      // including this one, so the success response carries a fresh
      // {token, refreshToken} pair. Without persisting it, the very next
      // authenticated request from this tab 401s on the now-revoked token.
      const currentUser = readCurrentUser() || {};
      if (
currentUser.user &&
currentUser.user._id === accountIdBeforeChange &&
data?.data?.token
      ) {
currentUser.token = data.data.token;
currentUser.refreshToken = data.data.refreshToken;
localStorage.setItem("currentUser", JSON.stringify(currentUser));
      }

      enqueueSnackbar(data?.data?.message, {
variant: "success",
      });
      onClose();
      return;
    }

    enqueueSnackbar(
      data.response ? data?.response?.data?.message : data?.data?.message,
      {
variant: "error",
      }
    );
  }}
>
  {({ errors, touched, isSubmitting }) => (
    <Form>
      <PasswordField
name="oldPassword"
label="Current Password"
placeholder="Enter Current Password"
shown={showOldPassword}
onToggle={() => { setShowOldPassword(!showOldPassword) }}
error={touched.oldPassword && errors.oldPassword}
      />
      <PasswordField
name="password"
label="New Password"
placeholder="Enter New Password"
shown={showNewPassword}
onToggle={() => { setShowNewPassword(!showNewPassword) }}
error={touched.password && errors.password}
      />
      <PasswordField
name="confirmPassword"
label="Confirm Password"
placeholder="Enter Confirm Password"
shown={showConfirmPassword}
onToggle={() => { setShowConfirmPassword(!showConfirmPassword) }}
error={touched.confirmPassword && errors.confirmPassword}
      />
      <div className="tw-mt-6 tw-text-center">
<Button type="submit" disabled={isSubmitting}>
  Change Password
</Button>
      </div>
    </Form>
  )}
</Formik>
      </DialogContent>
    </Dialog>
  );
}

const Profile = () => { // NOSONAR
  
  const [changePasswordPopupShow, setChangePasswordPopupShow] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [userData, setUserData] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showProfileLoader, setShowProfileLoader] = useState(false);
  const [countrycode, setCountryCodes] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('currentUser'))?.user;
  // Pooled inbox SP5: "has a plan" is the server's entitlement (the stored
  // copy, which every billing action and `entitlement changed` keep in step),
  // never selected_plan or payment_status. Without one, Profile offers
  // Choose your plan instead of the Subscription panel.
  const subscribed = hasChatAccess(currentUser);
  const isPaymentIncomplete = !subscribed;


  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Email and mobile are read-only (D2) and owned by the server, so they are
  // not validated here: a stored value in an older format must not block
  // saving a name change.
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .matches(/^[A-Za-z\s]*$/, "Name cannot contain numbers") // Ensure no numbers
      .test("valid-name", "Enter a valid name", (value) => {
        return !!value && value.trim().length > 0; // Ensure at least 1 valid character
      })
      .min(3, "Name must be at least 3 characters")
      .max(30, "Name must be at most 30 characters"),
  });
  const checkBlocked = async () => {
    await runBlockedCheck(navigate);
  }


  const fetchUserDetails = async () => {
    await fetchAndSetUserDetails(setShowProfileLoader, setUserData, navigate, enqueueSnackbar);
  };
  const fetchCountryCodes = async () => {
    const code = await getCountryCode();
    setCountryCodes(code?.data);
  };

  useEffect(() => {
    fetchCountryCodes();
    checkBlocked();
    fetchUserDetails();
  }, []); // eslint-disable-line


  const handleSubmit = async (values, { setSubmitting }) => {
    await submitProfileUpdate(values, setSubmitting, {
      setUserData,
      setPreviewImage,
      setShowEditProfile,
      fetchUserDetails,
      enqueueSnackbar,
    });
  };

  const handleRemoveProfileImage = async (setFieldValue, event) => {
    await removeProfileImageWithFeedback(setFieldValue, event, {
      previewImage,
      setPreviewImage,
      enqueueSnackbar,
      setUserData,
    });
  };

  
  const changePasswordPopupClose = () => setChangePasswordPopupShow(false);

  const openChangePasswordPopup = () => {
    
    setChangePasswordPopupShow(true);
  };

  

  const refressUser = (status) => {
    if (status) fetchUserDetails();
  }

  const [showEditProfile, setShowEditProfile] = useState(true);

  const currentUserType = JSON.parse(localStorage.getItem("currentUser"))?.user?.userType;

  const BENEFITS = [
    ["clock", "Save Time", "No more manual entry of debts & investments."],
    ["refresh", "Real-Time Sync", "Your balances and net worth always yp to date."],
    ["users", "Advisor Access", "Book meetings with financial experts anytime."],
    ["sparkle", "Smarter Insights", "Personalized planning based on verified data."],
  ];

  return (
    <Page>
      <PageHead
        title="Profile"
        subtitle="Every detail about you, right where it belongs."
        tools={
          currentUserType === 'client' && (
            <Button as={Link} variant="quiet" to="/questionnaire">
              Your questionnaire
            </Button>
          )
        }
      />

      {!showProfileLoader ? (
        <div className="tw-grid tw-items-start tw-gap-8 lg:tw-grid-cols-2">
          <div data-slot="col-5" className="tw-grid tw-min-w-0 tw-gap-6">
            <Panel as="section" aria-labelledby="profile-h" data-slot="profile-card">
              <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-3">
                <h2 id="profile-h" className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">Profile</h2>
                {showEditProfile && (
                  <Button
                    variant="icon"
                    size="sm"
                    iconOnly
                    aria-label="Edit profile"
                    data-slot="edit-profile-popup-btn"
                    onClick={() => setShowEditProfile(!showEditProfile)}
                  >
                    <StarsIcon name="edit" className="tw-h-4 tw-w-4 tw-shrink-0" />
                  </Button>
                )}
              </div>

              {showEditProfile ? (
                <div>
                  <div className="tw-mb-6 tw-flex tw-items-center tw-gap-4">
                    {userData?.profile_image ? (
                      <img
                        src={userData?.profile_image}
                        alt="Profile"
                        className="tw-h-14 tw-w-14 tw-shrink-0 tw-rounded-pill tw-object-cover"
                      />
                    ) : (
                      <span className="tw-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-pill tw-bg-surface-selected tw-text-lg tw-font-bold tw-text-text">
                        {userData?.firstName ? userData?.firstName.trim().charAt(0) : ""}
                        {userData?.lastName ? userData?.lastName.trim().charAt(0) : ""}
                      </span>
                    )}
                    <b
                      className="tw-min-w-0 tw-break-words tw-text-md"
                      title={`${userData?.firstName} ${userData?.lastName}`}
                    >
                      {userData?.firstName || userData?.lastName ? (
                        (() => {
                          const fullName = `${userData?.firstName} ${userData?.lastName}`;
                          const isTruncated = (userData?.firstName + userData?.lastName).length > 20;
                          return fullName.slice(0, 20) + (isTruncated ? "..." : "");
                        })()
                      ) : (
                        ""
                      )}
                    </b>
                  </div>
                  <dl className="tw-m-0 tw-grid tw-grid-cols-3 tw-gap-y-3 tw-text-sm">
                    <dt className="tw-text-text-muted">Email{" "}</dt>
                    <dd className="tw-col-span-2 tw-m-0 tw-break-words">{userData?.email ? `${userData?.email} ` : ""}</dd>
                    <dt className="tw-text-text-muted">Phone Number{" "}</dt>
                    <dd className="tw-col-span-2 tw-m-0 tw-tabular-nums">{userData?.mobile ? `${userData?.mobile} ` : ""}</dd>
                    <dt className="tw-text-text-muted">Password</dt>
                    <dd className="tw-col-span-2 tw-m-0">**********</dd>
                  </dl>
                </div>
              ) : (
                <div>
                  <Formik
                    initialValues={{
                      name: `${userData?.firstName || ""} ${userData?.lastName || ""}`,
                      email: userData?.email || "",
                      mobile: userData?.mobile || "",
                      profile_image: userData?.profile_image || "",
                      code: userData?.countryCode || "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                      <Form>
                        <div className="tw-relative tw-mb-6 tw-h-24 tw-w-24">
                          {shouldShowProfileImage(previewImage, userData) ? (
                            <>
                              <img
                                src={getProfileImageSrc(previewImage, userData)}
                                alt="Profile"
                                className="tw-h-24 tw-w-24 tw-rounded-pill tw-object-cover"
                              />
                              <Button
                                variant="icon"
                                size="sm"
                                iconOnly
                                data-slot="removeProfileImage"
                                className="tw-absolute tw-right-0 tw-top-0 tw-bg-surface"
                                onClick={(event) => handleRemoveProfileImage(setFieldValue, event)} // Wrap in a function
                              >
                                -
                              </Button>
                            </>
                          ) : (
                            <span className="tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-pill tw-bg-surface-selected tw-text-xl tw-font-bold tw-text-text">
                              {getProfileInitials(userData)}
                            </span>
                          )}
                          <input
                            type="file"
                            id="imageUpload"
                            accept=".png, .jpg, .jpeg"
                            aria-label="Change profile picture"
                            className="tw-peer tw-sr-only"
                            onChange={(event) => {
                              const file = event.currentTarget.files[0];
                              setFieldValue("profile_image", file); // Update Formik with the new image
                              setPreviewImage(URL.createObjectURL(file)); // Show image preview
                            }}
                          />
                          <label
                            htmlFor="imageUpload"
                            data-slot="avatar-edit"
                            className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-pill tw-bg-action tw-text-action-text hover:tw-bg-action-hover peer-focus-visible:tw-outline peer-focus-visible:tw-outline-2 peer-focus-visible:tw-outline-offset-2 peer-focus-visible:tw-outline-focus-ring"
                          >
                            <StarsIcon name="edit" className="tw-h-4 tw-w-4 tw-shrink-0" />
                          </label>
                        </div>

                        <Field label="Name" error={touched.name && errors.name}>
                          <FormikField
                            as={Input}
                            type="text"
                            id="name"
                            name="name"
                            placeholder="Enter your name"
                          />
                        </Field>
                        <Field label="Email">
                          <FormikField
                            as={Input}
                            type="email"
                            name="email"
                            id="email"
                            readOnly
                            aria-describedby="contact-locked-help"
                          />
                        </Field>

                        <div className="tw-mb-4 tw-grid tw-gap-2">
                          <label htmlFor="mobile" className="tw-text-sm tw-font-semibold tw-text-text">Phone Number</label>
                          <div className="tw-flex tw-gap-3">
                            <FormikField
                              as={Select}
                              name="code"
                              disabled
                              aria-label="Country code"
                              wrapperClassName="tw-w-28 tw-shrink-0"
                            >
                              {countrycode?.map((item) => (
                                <option key={item.code} value={item.code}>
                                  ({item.code})
                                </option>
                              ))}
                            </FormikField>
                            <div className="tw-min-w-0 tw-flex-1">
                              <FormikField
                                as={Input}
                                type="text"
                                id="mobile"
                                name="mobile"
                                readOnly
                                aria-describedby="contact-locked-help"
                                className="tw-tabular-nums"
                                value={formatPhoneNumber(values.mobile)}
                              />
                            </div>
                          </div>
                          <p id="contact-locked-help" className="tw-m-0 tw-text-xs tw-text-text-muted">
                            To change your email or phone, contact support.
                          </p>
                        </div>

                        <div className="tw-mb-2 tw-grid tw-gap-1 tw-text-sm">
                          <span className="tw-font-semibold">Password</span>
                          <span className="tw-text-text-muted">**********</span>
                          <Button variant="text" className="tw-justify-self-start" onClick={openChangePasswordPopup}>
                            Change Password
                          </Button>
                        </div>
                        <div className="tw-mt-8 tw-flex tw-flex-wrap tw-justify-end tw-gap-3">
                          <Button type="submit" variant="quiet" onClick={() => setShowEditProfile(!showEditProfile)}>
                            Cancel
                          </Button>
                          <Button type="submit" disabled={isSubmitting}>
                            Update Profile
                          </Button>
                        </div>

                      </Form>
                    )}
                  </Formik>
                </div>
              )}
            </Panel>
          </div>


          <div data-slot="col-7" className="tw-grid tw-min-w-0 tw-gap-6">
            {currentUserType === 'client' && (
              <article className="stars-dark tw-rounded-pass tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6 tw-text-text tw-shadow-2 max-rail:tw-p-4">
                {hasUnlimitedMeetings(userData) ? (
                  // UNLIMITED MEETINGS
                  <h3 className="tw-m-0 tw-text-xl tw-font-bold tw-text-text">
                    <span>Unlimited Meetings</span>
                  </h3>
                ) : (
                  // LIMITED MEETINGS. One number, not `consumed/total`: backend
                  // ARCH-036 removed both of those and there is no consumed/total
                  // split any more — the plan's annual allowance and the meetings
                  // the customer bought are separate buckets now, and what the
                  // heading has always promised is the sum of what is left. The
                  // card rendered " 0/" against the projected payload, since both
                  // sides of the fraction were `undefined`.
                  <h3 className="tw-m-0 tw-flex tw-items-baseline tw-justify-between tw-gap-4 tw-text-sm tw-font-regular tw-text-text-muted">
                    <span>Number of Meetings Left</span>
                    <> <strong className="tw-text-3xl tw-font-bold tw-tabular-nums tw-text-text">{meetingsAvailableCount(userData)}</strong></>
                  </h3>
                )}
              </article>
            )}
            {!subscribed && userData.userType === "client" && (
              <Panel title="Unlock Your Full Access">
                {/* SP6: the Plaid line went (linking needs no subscription on its own). Wording pending */}
                <p className="tw-mb-6 tw-mt-0 tw-text-sm tw-text-text-muted">Subscribe now to unlock premium advisor features.</p>
                <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                  {BENEFITS.map(([icon, title, body]) => (
                    <div key={title} className="tw-rounded-md tw-bg-surface-sunken tw-p-4">
                      <StarsIcon name={icon} className="tw-h-6 tw-w-6 tw-shrink-0 tw-text-text-accent" />
                      <h4 className="tw-mb-1 tw-mt-3 tw-text-md tw-font-bold tw-text-text">{title}</h4>
                      <p className="tw-m-0 tw-text-sm tw-text-text-muted">{body}</p>
                    </div>
                  ))}
                </div>
                <div className="tw-mt-6 tw-text-center">
                  <Button as={Link} to={CHOOSE_PLAN_PATH}>
                    <StarsIcon name="wallet" className="tw-h-4 tw-w-4 tw-shrink-0" />
                    Get Subscription
                  </Button>
                </div>
              </Panel>
            )}
            {currentUserType === 'client' && <ConsentsCard />}
            {/* SP6: Linked accounts, after Consents; never plan-gated (owner item 3). */}
            {currentUserType === 'client' && <LinkedAccountsCard />}
            {currentUserType === 'advisor' && <AdvisorReviewsCard />}
            {currentUserType === 'client' && !isPaymentIncomplete && <Subscription handleCancelStatus={refressUser} />}
          </div>
        </div>
      ) :
        (<Loader />)}


      <ChangePasswordModal
        show={changePasswordPopupShow}
        onClose={changePasswordPopupClose}
        enqueueSnackbar={enqueueSnackbar}
        showOldPassword={showOldPassword}
        setShowOldPassword={setShowOldPassword}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        showConfirmPassword={showConfirmPassword}
        setShowConfirmPassword={setShowConfirmPassword}
      />
    </Page>
  );
};

export default Profile;
